export const swedishPrivacyPolicy = `
# Integritetspolicy

Senast uppdaterad: 2025-11-10
Version: 2.1

---

## 1. Introduktion

Välkommen till taxiadministration.se. Vi tar din integritet på allvar och följer dataskyddslagstiftningen (GDPR).

**Personuppgiftsansvarig:**
- **Företag**: taxiadministration.se
- **Kontakt**: julius@baerell.com

---

## 2. Vilka Personuppgifter Vi Samlar In

**Kontouppgifter:**
- Email-adress, namn, lösenord (krypterat), Google User ID (vid Google Sign-In)

**Förar- och ekonomisk data:**
- Förarnamn, personnummer, kontaktinformation, körjournaler (CSV), intäkts och lönedata, fordonsuppgifter

**Teknisk data:**
- IP-adress, browser-typ, tidsstämplar

---

## 3. Varför Vi Samlar In Data (Laglig Grund)

**1. Tillhandahålla tjänsten**
- Laglig grund: Fullgörande av avtal (GDPR Art. 6.1.b)
- Beskrivning: Löneberäkningar, hantera förardata, generera rapporter

**2. Support och systemförbättringar**
- Laglig grund: Berättigat intresse (GDPR Art. 6.1.f)
- Beskrivning: Svara på frågor, anonymiserad statistik för produktutveckling

**3. Säkerhet**
- Laglig grund: Berättigat intresse (GDPR Art. 6.1.f)
- Beskrivning: Skydda systemet från missbruk och säkerhetshot

---

## 4. Hur Vi Använder Dina Uppgifter

- Utföra löneberäkningar och organisera förardata
- Generera lönespecifikationer och rapporter
- Skicka transaktionsemails och supportkommunikation
- Förbättra tjänsten genom anonymiserad statistik

---

## 5. Google API Services - Datainsamling och Användning

### 5.1 Översikt
taxiadministration.se använder Google Sign-In (via Firebase Authentication) för att ge dig ett enkelt och säkert sätt att logga in. När du väljer att logga in med Google får vi tillgång till viss information från ditt Google-konto.

### 5.2 Vilka Google-Data Vi Får Tillgång Till (Data Accessed)

Vi begär endast följande grundläggande information från Google:

**1. Email-adress**
- Din Google email
- Användning: Identifiera ditt konto och kommunikation

**2. Profilnamn**
- Ditt namn från Google-profilen
- Användning: Personalisera din upplevelse (visas i UI)

**3. Google User ID**
- Unikt ID från Google
- Användning: Teknisk identifiering av ditt konto

**4. Profilbild**
- Din Google-profilbild (URL)
- Användning: Visas i användargränssnittet (valfritt)

**Viktigt:**
- Vi begär ENDAST dessa grundläggande scopes: \`openid\`, \`email\`, \`profile\`
- Vi begär INTE tillgång till Gmail, Google Drive, Kalender eller andra Google-tjänster
- Vi läser INTE dina Google-emails eller filer

### 5.3 Hur Vi Använder Google-Data (Data Usage)

Vi använder informationen från Google Sign-In för följande:

**Autentisering och Kontoskapande:**
- Skapa och identifiera ditt taxiadministration.se-konto
- Logga in dig säkert utan separat lösenord
- Länka ditt Google-konto till ditt taxiadministration.se-konto

**Kommunikation:**
- Skicka supportmeddelanden och transaktionsemails till din Google email
- Kontakta dig om viktiga kontoändringar

**Användarupplevelse:**
- Visa ditt namn och profilbild i användargränssnittet
- Personalisera din dashboard

### 5.4 Delning och Överföring av Google-Data (Data Sharing)

**Vi delar INTE din Google-data med tredje part.**

- Ingen försäljning av Google-data
- Ingen delning med annonsörer
- Ingen delning med datamäklare
- Ingen användning för retargeting eller personaliserad annonsering
- Ingen användning för kreditvärdering

**Tekniska tredjepartsleverantörer:**
- **Firebase/Google Cloud** - Infrastruktur för authentication och datalagring (Google's egna tjänster)
- All Google-data lagras säkert i Firestore under ditt användarkonto

### 5.5 Datalagring och Bevarande (Data Retention)

- **Aktiva konton**: Google-data lagras så länge ditt konto är aktivt
- **Radering**: När du raderar ditt konto raderas all Google-data inom 90 dagar
- **Manuell radering**: Du kan när som helst begära omedelbar radering via julius@baerell.com

### 5.6 Datasäkerhet för Google-Data

- All data från Google lagras krypterat i Firebase Firestore
- Kommunikation sker över HTTPS (TLS-kryptering)
- Firebase Authentication följer Googles säkerhetsstandarder
- User-isolated databas - ingen användare kan se andras Google-data
- Regelbundna säkerhetsuppdateringar

### 5.7 Dina Rättigheter Över Google-Data

Du har rätt att:
- Se vilken Google-data vi har lagrat (kontakta julius@baerell.com)
- Radera din Google-data genom att radera ditt konto
- Återkalla taxiadministration.se:s tillgång via [Google Account Permissions](https://myaccount.google.com/permissions)
- Logga in med email/lösenord istället (inte med Google)

### 5.8 Google API Services - Limited Use Compliance

**Limited Use Statement:**

> taxiadministration.se's use of information received from Google APIs adheres to the [Google API Services User Data Policy](https://developers.google.com/terms/api-services-user-data-policy), including the Limited Use requirements.

Detta innebär att vi:
- Endast använder Google-data för att tillhandahålla vår löneberäkningstjänst
- INTE säljer eller delar Google-data för annonsering
- INTE använder Google-data för AI/ML-träning
- Följer alla begränsningar i Googles User Data Policy

---

## 6. Delning med Tredje Part

Vi använder Firebase (Google Cloud) för vår infrastruktur:
- **Firebase Authentication** - Inloggning och säkerhet
- **Firebase Firestore** - Databas för användardata
- **Firebase Hosting** - Webbserver

All data lagras inom EU/EEA enligt GDPR. Firebase är en del av Google Cloud och följer Googles säkerhetsstandarder.

---

## 7. Datalagring och Säkerhet

**Säkerhet:**
- HTTPS/TLS-kryptering för all kommunikation
- Krypterade lösenord via Firebase Authentication
- User-isolated databas (varje användare har isolerad data)
- Regelbundna säkerhetsuppdateringar

**Lagringstid:**
- **Aktiva konton**: Så länge kontot är aktivt
- **Raderade konton**: 90 dagar efter radering, sedan permanent raderad
- **Support-emails**: 2 år efter senaste kontakten

---

## 8. Cookies

Vi använder nödvändiga cookies för tjänstens funktion:
- **Autentiseringscookies** (Firebase Auth) - Håller dig inloggad
- **Session-cookies** - Håller reda på din session

Dessa cookies krävs för funktionalitet och kan inte avböjas.

---

## 9. Dina Rättigheter enligt GDPR

**Rätt till tillgång**: Be om en kopia av all data vi har om dig

**Rätt till rättelse**: Uppdatera felaktig information i ditt konto

**Rätt till radering**: Begär att vi raderar all din data

**Rätt till dataportabilitet**: Exportera din data i maskinläsbart format (Excel, JSON)

**Rätt att klaga**: Kontakta Integritetsskyddsmyndigheten (IMY) - www.imy.se

För alla GDPR-förfrågningar, kontakta julius@baerell.com. Vi svarar inom 30 dagar.

---

## 10. Ändringar

Vi kan uppdatera denna policy. Väsentliga ändringar meddelas via email minst 30 dagar i förväg.

---

## 11. Kontakta Oss

**Dataskyddsfrågor:**
- Email: julius@baerell.com
- Svartid: Inom 30 dagar enligt GDPR

---

**Senast granskad och uppdaterad: 2025-11-10**

För fullständig information om användarvillkor, se vår [Användarvillkor](#).
`

export const privacyVersion = "2.1"
export const privacyLastUpdated = "2025-11-10"
